import type { ReactNode } from "react";
import { useInRouterContext, useLocation } from "react-router-dom";
import { areaForPath } from "./route";
import { isValidTag } from "./vocabulary";

/** The current route's area, or null outside a router (some tests, the embed). */
function useArea(): string | null {
  const inRouter = useInRouterContext();
  // The hook order is stable: a component never moves in or out of a router.
  return inRouter ? areaForPath(useLocation().pathname) : null;
}

/** Props for a portal's root element. Required (not optional) so it fits a slot's props type. */
export interface PortalScopeProps {
  "data-ph-scope": string | undefined;
}

/**
 * The scope for content a portal renders outside the page DOM (MUI Menu,
 * Dialog, Popover, Drawer), so it misses PageScope (spec D7). It is the full
 * path from the route's area, so a component shown both in chat and in a
 * project chat is tagged `chat-…` or `projects-…` without knowing which.
 * Spread it on an element the portal already renders, such as a MUI Menu's
 * list (`slotProps={{ list: usePortalScope("model") }}`), where a wrapper div
 * would be invalid inside the <ul>. `section` must be a string literal.
 */
export function usePortalScope(section: string): PortalScopeProps {
  const area = useArea();
  if (!isValidTag(section)) {
    if (import.meta.env.DEV) console.warn(`[analytics] invalid portal section dropped: "${section}"`);
    return { "data-ph-scope": undefined };
  }
  return { "data-ph-scope": area ? `${area}-${section}` : section };
}

/**
 * `usePortalScope` as a wrapper, for portal content that is not a list (a
 * Dialog's body, a Popper's paper, the ui Menu's panel). Use it only inside a
 * portal: on the page, PageScope already gives the area.
 * `display: contents` keeps it out of layout.
 */
export function PortalScope({ section, children }: { section: string; children: ReactNode }) {
  return (
    <div {...usePortalScope(section)} style={{ display: "contents" }}>
      {children}
    </div>
  );
}
